import React from 'react';
import { cn } from '@/lib/utils';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetFooter,
} from '@/components/ui/sheet';
import { SubNavSection } from './SubNavSection';
import { SubNavActivities } from './SubNavActivities';
import { SubNavActivityDetails } from './SubNavActivityDetails';
import type { SubNavGroup } from './types';
import type { Activity, ActivityDetailsData } from '@/components/custom/ActivityMenu';

export interface SubNavSheetProps extends React.HTMLAttributes<HTMLDivElement> {
  navigationData: SubNavGroup[];
  activeItem?: string;
  activeTab?: string;
  onItemClick?: (groupTitle: string, itemHref: string) => void;
  side?: 'left' | 'right' | 'top' | 'bottom';
  showActivities?: boolean;
  recentActivities?: Activity[];
  activityDetails?: ActivityDetailsData;
  onViewAllActivities?: () => void;
  onOpenRecord?: () => void;
  onClose?: () => void;
  isOpen?: boolean;
  /** Custom link component for routing (e.g., React Router Link) */
  linkComponent?: React.ElementType;
}

export const SubNavSheet = React.forwardRef<HTMLDivElement, SubNavSheetProps>(
  ({ 
    navigationData, 
    activeItem,
    activeTab,
    onItemClick,
    side = 'left',
    showActivities = false,
    recentActivities = [],
    activityDetails,
    onViewAllActivities,
    onOpenRecord,
    onClose,
    isOpen = true,
    linkComponent,
    className,
    ...props 
  }, ref) => {
    
    const [expandedSections, setExpandedSections] = React.useState<string[]>(() => {
      const fallback = navigationData[0]?.title ?? 'Dashboard';
      const normalizedActive = activeTab
        ? navigationData.find(g => g.title.toLowerCase() === activeTab.toLowerCase())?.title
        : undefined;
      const initial = normalizedActive || fallback;
      return initial ? [initial] : [];
    });
    const isBottomSheet = side === 'bottom';

    if (!navigationData || navigationData.length === 0) {
      return null;
    }

    const toggleSection = (section: string) => {
      setExpandedSections(prev => 
        prev.includes(section) 
          ? prev.filter(s => s !== section)
          : [...prev, section]
      );
    };

    // Ensure activeTab section is expanded when it changes
    React.useEffect(() => {
      if (!activeTab) return;
      const normalized = navigationData.find(g => g.title.toLowerCase() === activeTab.toLowerCase())?.title;
      if (!normalized) return;
      setExpandedSections((prev) => (prev.includes(normalized) ? prev : [...prev, normalized]));
    }, [activeTab, navigationData]);

    return (
      <Sheet open={isOpen} onOpenChange={(open: boolean) => { if (!open) onClose?.(); }}>
        <SheetContent 
          side={side} 
          className={cn(
            "bg-white p-0",
            isBottomSheet ? "h-[80vh] w-full" : "w-[350px]",
            className
          )}
          ref={ref}
          {...props}
        >
          {/* Navigation Sections */}
          <div className="flex flex-col h-full">
            <div className="flex-1 overflow-y-auto pt-14 pb-6 px-6">
              <div className="space-y-0">
                {navigationData.map((group, index) => (
                  <SubNavSection
                    key={group.title}
                    title={group.title}
                    items={group.items}
                    activeItem={activeItem}
                    isExpanded={expandedSections.includes(group.title)}
                    onToggle={() => toggleSection(group.title)}
                    onItemClick={onItemClick}
                    showBorder={index !== navigationData.length - 1}
                    variant="sheet"
                    linkComponent={linkComponent}
                  />
                ))}

                {/* Recent Activities Section */}
                {showActivities && recentActivities.length > 0 && (
                  <SubNavActivities
                    activities={recentActivities}
                    isExpanded={expandedSections.includes('Recent Activities')}
                    onToggle={() => toggleSection('Recent Activities')}
                    onViewAll={onViewAllActivities}
                    variant="sheet"
                  />
                )}

                {/* Activity Details Section */}
                {showActivities && activityDetails && (
                  <SubNavActivityDetails
                    details={activityDetails}
                    isExpanded={expandedSections.includes('Activity Details')}
                    onToggle={() => toggleSection('Activity Details')}
                    onOpenRecord={onOpenRecord}
                    variant="sheet"
                  />
                )}
              </div>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    );
  }
);

SubNavSheet.displayName = 'SubNavSheet';
